/* eslint-disable @typescript-eslint/indent */
import React, { useEffect, useRef, useState } from 'react';
import logger from '@/logger';
import MasterclassDescriptionPage from '@/components/MasterclassDescriptionPage';
import MasterClassDesc from '@/api/masterclassDescription';
import Spinner from '@/components/common/Loader';
import CourseDescription from '@/api/courseDescription';
// import UserDetailsContext from '@/context/UserDetailsContext';
import Head from 'next/head';
import useMasterclassDescriptionStore from '@/store/MasterClassDescription/useMasterclasDescriptionStore';
import getMasterclassCanonicalUrl from '@/utils/masterclassCanonical.utils';
import { companyImage } from '@/utils/common/env.constants';
import { IFaqsDetails } from '@/interfaces/homePage';
import { useRouter } from 'next/router';
import moEvent from '@/utils/moEngage';
import {
  addToGoogleCalender,
  getDateAndTime,
  getInstructorNameAndCompany,
  MASTERCLASS_TYPES,
} from '@/utils/masterclass/masterclass';
import { useUTMParameters } from '@pwskills/rachnaui/hooks';
import { GAevent } from '@/utils/firebase.utils';
import BaseLayout from '@/components/common/BaseLayout';
import { ICategory } from '@pwskills/rachnaui/components/Navbar/Navbar.types';
import { IError } from '@/interfaces/common';
import HomePageApi from '@/api/homePage';
import {
  getRegistrationStatus,
  MASTERCLASS_REGISTRATION_AND_CLASS_STATUS,
} from '@/utils/common/masterclassDescription';
import useUserDetails from '@/store/userDetails';
import useMasterclassCountdownTimer from '@/hooks/useMasterclassCountdownTimer';
import {
  IMasterclassDescription,
  IMasterclassDescriptionResponse,
} from '@/interfaces/masterclassDescription';

const MasterClassDescriptionPage = ({
  response,
  canonicalUrl,
}: {
  response: any;
  slug: string;
  canonicalUrl: string;
}) => {
  const { data } = response;
  const { courseId: masterclassId } = data;
  // const { isLoggedIn, user, isLoading } = useContext(UserDetailsContext);
  const {
    userDetails: user,
    userIsLoggedIn: isLoggedIn,
    profileLoader: isLoading,
  } = useUserDetails();
  const router = useRouter();
  const { type } = router.query;
  const utmQuery = useUTMParameters();
  const dataLayerRef = useRef(false);

  const masterclassDetails = useMasterclassDescriptionStore((state) => state.masterclassDetails);
  const setMasterclassDetails = useMasterclassDescriptionStore(
    (state) => state.setMasterclassDetails
  );

  const setIsLoading = useMasterclassDescriptionStore((state) => state.setIsLoading);
  const setIsMasterclassBought = useMasterclassDescriptionStore(
    (state) => state.setIsMasterclassBought
  );

  const [isCourseBought, setIsCourseBought] = useState(false);
  const [isCourseBoughtLoading, setIsCourseBoughtLoading] = useState(true);

  const { masterclassTimings } = masterclassDetails || {};

  const { startTime, endTime } = masterclassTimings || {};

  const registrationStatus = getRegistrationStatus({
    startDate: startTime as string,
    endDate: endTime as string,
  });

  const transformedMasterclassResponse = (
    masterclassDescriptionResponse: IMasterclassDescriptionResponse
  ): IMasterclassDescription => {
    return {
      ...masterclassDescriptionResponse,
      masterclassId: masterclassDescriptionResponse?.courseId || '',
      title: masterclassDescriptionResponse?.title || masterclassDescriptionResponse?.name || '',
      _id: masterclassDescriptionResponse?._id || '',
      about: {
        descriptionSections: masterclassDescriptionResponse?.about?.descriptionSections || [],
        coursePointers: masterclassDescriptionResponse?.about?.coursePointers || [],
      },
      mobilePricing: masterclassDescriptionResponse?.mobilePricing || {
        pricingOnCardHero: '',
        ctaOnCard: '',
      },
      coursePurchaseType: masterclassDescriptionResponse?.coursePurchaseType || '',
      makeActiveForPurchase: masterclassDescriptionResponse?.makeActiveForPurchase || false,
      isProgramFeatured: masterclassDescriptionResponse?.isProgramFeatured || false,
      enableAboutCourse: masterclassDescriptionResponse?.enableAboutCourse || false,
      courseType: masterclassDescriptionResponse?.courseType || '',
      onlineDegreeType: masterclassDescriptionResponse?.onlineDegreeType || '',
      isJobGuaranteeProgram: masterclassDescriptionResponse?.isJobGuaranteeProgram || false,
      isJobAssistanceProgram: masterclassDescriptionResponse?.isJobAssistanceProgram || false,
      categoryIncludingParents: masterclassDescriptionResponse?.categoryIncludingParents || [],
      active: masterclassDescriptionResponse?.active || false,
      platformType: masterclassDescriptionResponse?.platformType || '',
      showOnHomePage: false,
      npfTrackingEnable: false,
      checkoutFlowType: masterclassDescriptionResponse?.checkoutFlowType || '',
      pageTheme: masterclassDescriptionResponse?.pageTheme || '',
      slug: masterclassDescriptionResponse?.slug || '',
      canonicalTag: masterclassDescriptionResponse?.config?.canonicalTag || '',
      registration: {
        startDate: masterclassDescriptionResponse?.registrationStartDate || '',
        endDate: masterclassDescriptionResponse?.registrationEndDate || '',
      },
      seo: {
        title: masterclassDescriptionResponse?.seo?.title || '',
        description: masterclassDescriptionResponse?.seo?.description || '',
        keywords: masterclassDescriptionResponse?.seo?.keywords || '',
        ogTitle: masterclassDescriptionResponse?.seo?.ogTitle || '',
        twitterTitle: masterclassDescriptionResponse?.seo?.twitterTitle || '',
        ogDescription: masterclassDescriptionResponse?.seo?.ogDescription || '',
        twitterDescription: masterclassDescriptionResponse?.seo?.twitterDescription || '',
        ogImage: masterclassDescriptionResponse?.seo?.ogImage || '',
        twitterImage: masterclassDescriptionResponse?.seo?.twitterImage || '',
      },
      faq: masterclassDescriptionResponse?.faq || [],
      description: masterclassDescriptionResponse?.shortDescription || '',
      batches: masterclassDescriptionResponse?.batches || [],
      masterclassTimings: {
        showStartDateOnCard: false,
        startTime: masterclassDescriptionResponse?.startDate || '',
        endTime: masterclassDescriptionResponse?.endDate || '',
      },
      liveVideoLink: masterclassDescriptionResponse?.config?.liveVideoLink || '',
      heroSectionMeta: {
        companyLogo: masterclassDescriptionResponse?.heroSectionMeta?.companyLogo || [],
        _id: masterclassDescriptionResponse?.heroSectionMeta?._id || '',
        title: masterclassDescriptionResponse?.heroSectionMeta?.title || '',
        image: masterclassDescriptionResponse?.heroSectionMeta?.image || '',
        description: masterclassDescriptionResponse?.heroSectionMeta?.description || '',
        bgImage: masterclassDescriptionResponse?.heroSectionMeta?.bgImage || '',
      },
      masterclassKeyFactors: masterclassDescriptionResponse?.masterclassKeyFactors || [],
      glimpseOfMasterClass: masterclassDescriptionResponse?.glimpseOfMasterClass || [],
      whoShouldEnroll: masterclassDescriptionResponse?.whoShouldEnroll || [],
      whatYouWillLearn: masterclassDescriptionResponse?.whatYouWillLearn || [],
      communityLink: masterclassDescriptionResponse?.communityLink || '',
      img: masterclassDescriptionResponse?.img || '',
      certificateType: masterclassDescriptionResponse?.certificateType || [],
    };
  };

  const fetchStartDate = () => {
    const istFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'Asia/Kolkata' });
    const istDate =
      response?.data?.masterclassTimings?.startDate &&
      istFormatter?.format(new Date(response?.data?.masterclassTimings?.startDate));
    return istDate;
  };

  const [isUserShownDescriptionPage, setIsUserShownDescriptionPage] = useState(false);
  const [categoryData, setCategoryData] = useState<ICategory[]>([]);
  const { isTimerEnded } = useMasterclassCountdownTimer(startTime);
  const isMasterclassBought = useMasterclassDescriptionStore(
    (state: { isMasterclassBought: boolean }) => state.isMasterclassBought
  );
  const [isClassTimerEnded, setIsTimerEnded] = useState(isTimerEnded);

  useEffect(() => {
    setIsTimerEnded(isTimerEnded);
  }, [isTimerEnded, isMasterclassBought]);

  // const useStore = useMemo(() => {
  //   return {
  //     masterclassDetails,
  //     setMasterclassDetails,
  //     loading: isLoading,
  //     setIsLoading,
  //     setIsMasterclassBought,
  //     isMasterclassBought: false,
  //   };
  // }, [masterclassDetails, isMasterclassBought, isLoading]);

  useEffect(() => {
    setMasterclassDetails(transformedMasterclassResponse(response.data));
  }, [response.data]);

  const checkIsMasterclassBought = async () => {
    setIsCourseBoughtLoading(true);
    if (masterclassId) {
      try {
        const courseBoughtResponse = await CourseDescription.isCourseBought(masterclassId);
        if (courseBoughtResponse?.success) {
          setIsLoading(false);
          setIsMasterclassBought(true);
          setIsCourseBought(true);
        } else {
          setIsMasterclassBought(false);
          setIsCourseBought(false);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      } finally {
        setIsCourseBoughtLoading(false);
      }
    }
  };

  useEffect(() => {
    if (masterclassId && isLoggedIn && user.id) {
      checkIsMasterclassBought();

      // call api
      MasterClassDesc.getMasterclassPageVisitNPF(utmQuery, masterclassId);
    }
  }, [masterclassId, isLoggedIn, user.id]);

  const sendDataToGAEvent = () => {
    const GAEventData = {
      user_id: user?._id,
      user_type: isLoggedIn ? 'loggedin' : 'not_loggedin',

      page_ref: router.query.page_ref,
      source: router.query.position || '',

      click_text: router.query.click_text,
      category_name: masterclassDetails?.categoryId?.parent?.title || '',
      category_id: masterclassDetails?.categoryIncludingParents?.[1] || '',
      course_id: masterclassDetails?.masterclassId,
      course_name: masterclassDetails?.title || masterclassDetails?.name,
      course_type: 'masterclass',
      utm_source: utmQuery?.utm_source,
      utm_medium: utmQuery?.utm_medium,
      utm_campaign: utmQuery?.utm_campaign,
      page_name: 'course_description_page',
    };
    GAevent('sk_page_visit', GAEventData);
  };

  useEffect(() => {
    if (masterclassDetails?._id && !isLoading && router.isReady && !dataLayerRef.current) {
      dataLayerRef.current = true;
      sendDataToGAEvent();
    }
  }, [masterclassDetails?._id, isLoading, router.isReady, dataLayerRef.current]);

  const handleInActiveCourses = () => {
    setIsUserShownDescriptionPage(false);
    router.push('/masterclass');
  };

  useEffect(() => {
    if (masterclassDetails?._id && !isLoading && router.isReady) {
      // masterclass active
      if (masterclassDetails) {
        setIsUserShownDescriptionPage(true);
      }
      // masterclass inactive
      // logged in
      else if (isLoggedIn) {
        // check if course bought
        if (!isCourseBoughtLoading) {
          // isCourse bought
          if (isCourseBought) {
            setIsUserShownDescriptionPage(true);
          }
          // isCourse not bought
          else {
            handleInActiveCourses();
          }
        }
      }
      // not logged in
      else {
        handleInActiveCourses();
      }
    }
  }, [
    masterclassDetails?._id,
    isLoading,
    router.isReady,
    isLoggedIn,
    isCourseBought,
    isCourseBoughtLoading,
  ]);

  const fetchCategoryDetails = async () => {
    try {
      const catchResponse = await HomePageApi.getCompanyAndFaqs();

      if (catchResponse) {
        const categoriesDetails = catchResponse?.data?.categories;
        setCategoryData(categoriesDetails);
      }
    } catch (error: unknown) {
      // eslint-disable-next-line no-console
      console.error((error as IError)?.error);
    }
  };

  useEffect(() => {
    fetchCategoryDetails();
  }, []);

  // moEngage Event
  useEffect(() => {
    if (masterclassDetails?._id && router?.isReady && user?._id) {
      const moEventData = {
        user_id: user?._id || '',
        username: `${user?.firstName} ${user?.lastName}` || '',
        phone_number: user?.phoneNumber || '',
        user_type: isLoggedIn ? 'loggedin' : 'not_loggedin',
        source:
          type === MASTERCLASS_TYPES.MARKETING ? 'marketing landing page' : router.query.position,
        masterclass_type: masterclassDetails?.pricing?.isFree ? 'Free' : 'Paid',
        masterclass_name: masterclassDetails?.title || masterclassDetails?.name,
        masterclass_date: getDateAndTime(masterclassDetails).date,
        masterclass_time: getDateAndTime(masterclassDetails).time,
        masterclass_id: masterclassId,
        calendar_url: addToGoogleCalender(
          masterclassDetails.slug,
          masterclassId,
          masterclassDetails.title || masterclassDetails.name || '',
          masterclassDetails.masterclassTimings,
          masterclassDetails.startDate,
          masterclassDetails.endDate
        ),
        speaker_name: getInstructorNameAndCompany(masterclassDetails).instructorName,
        speaker_company: getInstructorNameAndCompany(masterclassDetails).pastCompanies,
        speaker_background: getInstructorNameAndCompany(masterclassDetails).instructorDescription,
        price: masterclassDetails?.pricing?.IN || 0,
        category: masterclassDetails?.categoryId?.parent?.title || '',
        ...(user?.verified && user?.email && { email: user.email }),
      };
      moEvent('masterclass_details_page_visit', moEventData);
    }
  }, [masterclassDetails?._id, user, router?.isReady]);

  const structData = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: response?.data?.faq?.map((faq: IFaqsDetails) => {
      return {
        '@type': 'Question',
        name: faq?.question,
        acceptedAnswer: {
          '@type': 'Answer',
          text: faq?.answer,
        },
      };
    }),
  };

  const strucDataBreadCrumb = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Home',
          item: 'https://pwskills.com',
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: `${response?.data?.title}`,
          item: `https://pwskills.com/courses/${response?.data?.slug}`,
        },
      ],
    },
  ];

  const structDataVideo = {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: response?.data?.title,
    description: response?.data?.description,
    // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
    thumbnailUrl:
      response?.data?.img && response?.data?.img.startsWith('https')
        ? response?.data?.img
        : `${companyImage}/thumbnails/${response?.data?.img}`,
    // uploadDate: response.data.registration.startDate,
    duration: '',
    contentUrl: response?.data?.videoURL,
    embedUrl: response?.data?.videoURL,
    interactionStatistic: {
      '@type': 'InteractionCounter',
      interactionType: { '@type': 'WatchAction' },
      userInteractionCount: '',
    },
    regionsAllowed: 'IN,US',
  };

  const structDataEducationEvent = {
    '@context': 'http://schema.org',
    '@type': 'EducationEvent',
    name: response?.data?.name || response?.data?.title,
    description: response?.data?.description,
    image: response?.data?.img,
    performer: 'PW Skills',
    offers: response?.data?.pricing?.IN
      ? {
          '@type': 'AggregateOffer',
          lowPrice: Math.floor(
            (response?.data?.pricing?.IN || 0) -
              (Math.abs(response?.data?.pricing?.discount || 0) *
                (response?.data?.pricing?.IN || 0)) /
                100
          ),
          highPrice: response?.data?.pricing?.IN,
          url: `https://pwskills.com/course/${response?.data?.slug}/`,
          availability: 'https://schema.org/InStock',
          price: Math.floor(
            (response?.data?.pricing?.IN || 0) -
              (Math.abs(response?.data?.pricing?.discount || 0) *
                (response?.data?.pricing?.IN || 0)) /
                100
          ),
          priceCurrency: 'INR',
          validFrom: '',
        }
      : {
          '@type': 'Offer',
          price: 0,
          priceCurrency: 'INR',
          availability: 'https://schema.org/InStock',
        },
    startDate: fetchStartDate(),
    // endDate: response.data.registration.endDate,
    url: `https://pwskills.com/course/${response.data.slug}/`,
    duration:
      response?.data?.keyValueMetaInfo?.Duration ||
      response?.data?.meta?.find((item: any) => item.title === 'Duration')?.subText ||
      '',
    eventAttendanceMode: 'https://schema.org/OnlineEventAttendanceMode',
    eventStatus: 'http://schema.org/EventScheduled',
    location: {
      '@type': 'VirtualLocation',
      url: `https://pwskills.com/course/${response.data.slug}/`,
    },
    organizer: {
      '@type': 'Organization',
      name: 'PW Skills',
      url: 'https://pwskills.com/',
    },
  };

  const structCourseList = {
    '@id': '',
    '@context': 'https://schema.org',
    type: 'Course',
    name: `${response?.data?.title}`,
    description: `${response?.data?.seo?.description}`,
    provider: {
      '@type': 'Organization',
      name: 'PW Skills',
      sameAs: `https://pwskills.com/course/${response?.data?.slug}`,
    },
    offers: [
      {
        '@type': 'Offer',
        category: response?.data?.pricing?.isFree ? 'Free' : 'Paid',
        priceCurrency: 'INR',
        price: response?.data?.pricing?.IN
          ? Math.floor(
              (response?.data?.pricing?.IN || 0) -
                (Math.abs(response?.data?.pricing?.discount || 0) *
                  (response?.data?.pricing?.IN || 0)) /
                  100
            )
          : 0,
      },
    ],
    hasCourseInstance: [
      {
        '@type': 'CourseInstance',
        courseMode: 'Online',
        courseWorkload: 'P2D',
      },
    ],
  };

  return (
    <>
      <Head>
        <meta name="description" content={response?.data?.seo?.description} />

        {/* Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta
          property="og:image"
          content={
            response?.data?.seo?.ogImage && response?.data?.seo?.ogImage.trim() !== ''
              ? response?.data?.seo?.ogImage
              : // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
              response?.data?.img && response?.data?.img.startsWith('https')
              ? response?.data?.img
              : `${companyImage}/thumbnails/${response?.data?.img}`
          }
        />
        <meta
          property="og:title"
          content={
            response?.data?.seo?.ogTitle ? response?.data?.seo?.ogTitle : response?.data?.seo?.title
          }
        />
        <meta
          property="og:description"
          content={
            response?.data?.seo?.ogDescription
              ? response?.data?.seo?.ogDescription
              : response?.data?.seo?.description
          }
        />
        <meta property="og:image:width" content="456" />
        <meta property="og:image:height" content="256" />

        {/* Twitter */}
        <meta property="twitter:url" content={canonicalUrl} />
        <meta
          property="twitter:title"
          content={
            response?.data?.seo?.twitterTitle
              ? response?.data?.seo?.twitterTitle
              : response?.data?.seo?.title
          }
        />
        <meta
          property="twitter:description"
          content={
            response?.data?.seo?.twitterDescription
              ? response?.data?.seo?.twitterDescription
              : response?.data?.seo?.description
          }
        />
        <meta
          property="twitter:image"
          content={
            response?.data?.seo?.twitterImage && response?.data?.seo?.twitterImage.trim() !== ''
              ? response?.data?.seo?.twitterImage
              : // upload thumbnail logic is updated - newer uploads will return s3 bucket url and older one will return id
              response?.data?.img && response?.data?.img.startsWith('https')
              ? response?.data?.img
              : `${companyImage}/thumbnails/${response?.data?.img}`
          }
        />
        <meta property="twitter:image:width" content="456" />
        <meta property="twitter:image:height" content="256" />
        <script
          key="course-description-faqs"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structData) }}
        />
        <script
          key="course-description-breadcrumbs"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(strucDataBreadCrumb) }}
        />
        <script
          key="course-description-video-schema"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structDataVideo) }}
        />
        <script
          key="course-description-education-event"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structDataEducationEvent) }}
        />
        <script
          key="course-description-course-list"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structCourseList) }}
        />
      </Head>
      {/* description page */}
      {masterclassDetails && isUserShownDescriptionPage ? (
        type === MASTERCLASS_TYPES.MARKETING ? (
          <div
            className={`${
              registrationStatus !== MASTERCLASS_REGISTRATION_AND_CLASS_STATUS.CLASS_ENDED &&
              'pb-[72px]'
            } medium:pb-[98px] large:pb-[100px]`}
          >
            <MasterclassDescriptionPage
              masterclassDetails={masterclassDetails}
              isClassTimerEnded={isClassTimerEnded}
              isMasterclassBought={isMasterclassBought}
            />
          </div>
        ) : (
          <BaseLayout category={categoryData} pageName="masterclass_description">
            <MasterclassDescriptionPage
              masterclassDetails={masterclassDetails}
              isClassTimerEnded={isClassTimerEnded}
              isMasterclassBought={isMasterclassBought}
            />
          </BaseLayout>
        )
      ) : (
        <Spinner />
      )}
    </>
  );
};

export const getStaticPaths = async () => {
  return { paths: [], fallback: 'blocking' };
};

export async function getStaticProps({ params }: { params: { masterclassSlug: string } }) {
  try {
    const response = await MasterClassDesc.getMasterclassDescription(params.masterclassSlug);
    logger.info(`GET /masterclass/${params.masterclassSlug}`);
    return {
      props: {
        slug: params.masterclassSlug,
        response,
        canonicalUrl: getMasterclassCanonicalUrl(
          (response as { data?: Parameters<typeof getMasterclassCanonicalUrl>[0] })?.data,
          params.masterclassSlug
        ),
      },
      revalidate: 10, // 10 seconds
    };
  } catch (error: any) {
    logger.error(`GET /masterclass/${params.masterclassSlug} ${error.error ? error.error : error}`);
    return { notFound: true, revalidate: 1 };
  }
}

export default MasterClassDescriptionPage;
